import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  profileImageFileSize,
  profileImageSpec,
  type ArtistProfileResponse,
} from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { useMarkProfileAssetMutation } from '../../../../../redux/api/fymAPI';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { getFullAPIFileURL } from '../../../../common/getFullAPIFileURL';
import { profileDownloadUrl, profileFileUrl, sourceLabel } from '../profileAssets';
import { profileMessages as m } from '../profileMessages';
import { profileSlotOf } from '../profileSlots';
import type { ProfileImageTool } from '../profileRoute';
import type { ProfileRoute } from '../useProfileRoute';
import { ProfileDownloadLink } from '../page/ProfileDownloadLink';
import { ProfileUpgradeButton } from '../page/ProfileUpgradeButton';
import { spotifyButtonMode } from '../spotify/spotifyUploadState';
import { filesPerMake } from './profileToolDraft';
import { BLOCKER_TEXT, TOOL_COPY } from './profileToolCopy';
import { ProfileToolPreview } from './ProfileToolPreview';
import { ProfileToolRail } from './ProfileToolRail';
import { ProfileToolSwitch } from './ProfileToolSwitch';
import type { useProfileTool } from './useProfileTool';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileToolRailPartProps {
  profile: ArtistProfileResponse;
  tool: ProfileImageTool;
  state: ReturnType<typeof useProfileTool>;
  route: ProfileRoute;
}

/** The rail of one tool, filled from the tool state (design b1–b4). */
const ProfileToolRailPartComponent: React.FC<ProfileToolRailPartProps> = ({ profile, tool, state, route }) => {
  const intl = useIntl();
  const [mark, markState] = useMarkProfileAssetMutation();
  const { kind, draft, change, slot } = state;
  const spec = profileImageSpec(kind);
  const files = filesPerMake(tool, draft);
  const { current } = slot;
  const download = profileDownloadUrl(current);
  const mode = spotifyButtonMode(profile);
  // The round photo of the previews: the Profile artist photo, else the Music24 photo (Settings → Artist).
  const avatarUrl =
    profileFileUrl(profileSlotOf(profile.assets, 'spotifyAvatar').current) ??
    (profile.avatar === null ? state.artist.avatarUrl : getFullAPIFileURL(profile.avatar.url));

  const meta = useMemo(() => {
    const size = profileImageFileSize(spec, profile.preview);
    const chips = [
      files === 1
        ? `${size.width} x ${size.height}`
        : intl.formatMessage(files === 2 ? t.metaTwoFiles : t.metaThreeFiles),
      intl.formatMessage(t.metaJpg),
    ];
    if (spec.coverMode === 'blur') {
      // The Spotify header never has text; the banners carry the text of the draft (design profile-card b).
      const withText = tool === 'banners' && draft.bannerText !== 'none';
      chips.push(intl.formatMessage(withText ? t.metaWithText : t.metaNoText));
    }
    chips.push(
      draft.source === 'cover' && state.release !== undefined
        ? state.release.title
        : intl.formatMessage(sourceLabel(draft.source, spec.coverMode === 'plain'))
    );
    if (profile.preview) {
      chips.push(intl.formatMessage(t.metaPreview));
    }
    return chips;
  }, [draft.bannerText, draft.source, files, intl, profile.preview, spec, state.release, tool]);

  const theme = (id: keyof typeof releaseThemeMessages) => intl.formatMessage(releaseThemeMessages[id].name);
  let coloursLine = intl.formatMessage(t.coloursNone);
  if (state.coloursFrom === 'artist') {
    coloursLine = intl.formatMessage(t.coloursArtist, { theme: theme(profile.look.themeId) });
  } else if (state.coloursFrom === 'release' && state.release?.look !== undefined && state.release.look !== null) {
    coloursLine = intl.formatMessage(t.coloursRelease, {
      title: state.release.title,
      theme: theme(state.release.look.themeId),
    });
  }

  const guideNote =
    tool === 'pressPhoto' && profile.preview
      ? intl.formatMessage(t.pressGuideNoteFree)
      : intl.formatMessage(TOOL_COPY[tool].guideNote);
  const switches =
    tool === 'banners' ? (
      <ProfileToolSwitch
        id="profile-tool-same"
        label={intl.formatMessage(t.sameForAll)}
        checked={draft.sameForAll}
        onChange={(sameForAll) => change({ sameForAll })}
      />
    ) : tool === 'spotifyAvatar' ? (
      <ProfileToolSwitch
        id="profile-tool-music24-photo"
        label={intl.formatMessage(t.useAsMusic24Photo)}
        checked={draft.setAsAccountAvatar}
        onChange={(setAsAccountAvatar) => change({ setAsAccountAvatar })}
      />
    ) : undefined;
  const isSpotify = tool === 'spotifyHeader' || tool === 'spotifyAvatar';

  return (
    <ProfileToolRail
      preview={
        <ProfileToolPreview
          tool={tool}
          groundOf={state.groundOf}
          colours={state.colours}
          watermark={profile.preview}
          artistName={profile.spotify.artistName ?? state.artist.name ?? ''}
          avatarUrl={avatarUrl}
          bannerText={tool === 'banners' ? state.bannerPreview : null}
        />
      }
      switches={switches}
      guideNote={guideNote}
      meta={meta}
      colours={state.colours}
      coloursLine={coloursLine}
      makeLabel={intl.formatMessage(files === 1 ? t.makeIt : files === 2 ? t.makeBoth : t.makeAllThree)}
      canMake={state.blocker === null}
      isMaking={state.make.isBusy}
      blockedReason={state.blocker === null ? null : intl.formatMessage(BLOCKER_TEXT[state.blocker])}
      feedback={state.make.feedback}
      madeNote={state.made ? intl.formatMessage(t.madeNote) : null}
      onMake={state.submit}
      actions={
        <>
          {download !== null && <ProfileDownloadLink href={download} testId="profile-tool-download" />}
          {isSpotify && mode === 'send' && current !== null && (
            <SMLButton className={styles.sm} onClick={route.openSheet}>
              <span className={styles.spotDot} aria-hidden="true" />
              {intl.formatMessage(m.uploadToSpotify)}
            </SMLButton>
          )}
          {isSpotify && mode === 'upgrade' && <ProfileUpgradeButton label={m.upgradeToSend} small />}
          {tool === 'banners' && current !== null && (
            <SMLButton
              className={styles.sm}
              disabled={markState.isLoading}
              onClick={() => {
                mark({ accountId: state.accountId, assetUuid: current.uuid, set: current.setAt === null }).catch(
                  console.error
                );
              }}
            >
              {intl.formatMessage(current.setAt === null ? m.iSetIt : m.notSet)}
            </SMLButton>
          )}
        </>
      }
    />
  );
};

export const ProfileToolRailPart = TypedMemo(ProfileToolRailPartComponent);
